import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { isTooManyRequestsError, storableError } from '../../util/errors';
import { attachInboxListenerFeedback } from '../../util/inboxListenerFeedback';
import { retryOn429 } from '../../util/sharetribeSafeQuery';
import { parse } from '../../util/urlHelpers';
import { getSupportedProcessesInfo } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { loadData as loadDashboardData } from '../DashboardPage/DashboardPage.duck';
import { bookingsViewFromSearch, BOOKINGS_VIEW_CALENDAR } from '../../util/bookingsView';
import {
  inboxQueryLastTransitions,
  mergeInboxTransactions,
  normalizeInboxSort,
  UPCOMING_LAST_TRANSITIONS,
} from '../../util/inboxSort';

const INBOX_PAGE_SIZE = 25;

const emptyInboxMeta = (page = 1) => ({
  page,
  perPage: INBOX_PAGE_SIZE,
  totalItems: 0,
  totalPages: 1,
});

/**
 * True when a failed transactions query is just “nothing here”, not a load outage.
 *
 * @param {Object} [error]
 * @returns {boolean}
 */
export const isEmptyInboxFetchError = error => {
  if (!error) {
    return false;
  }
  const status = error.status || error.data?.status;
  if (status === 404) {
    return true;
  }
  const message = String(error.message || error.statusText || '').toLowerCase();
  return (
    message.includes('not found') ||
    message.includes('no transaction') ||
    message.includes('no results')
  );
};

const listFromQueryResponse = response => {
  const raw = response?.data?.data;
  if (Array.isArray(raw)) {
    return raw;
  }
  return raw ? [raw] : [];
};

// ================ Helper functions ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const lastMessagePreview = message => {
  const content = message?.attributes?.content;
  return typeof content === 'string' ? content.trim() : '';
};

/**
 * Newest message preview per transaction. Uses camelCase `transactionId`
 * (same fix as TransactionPage.fetchMessages). Failures stay empty so the
 * inbox list still renders.
 *
 * @param {Object} sdk
 * @param {Array<Object>} transactions
 * @returns {Promise<Object<string, string>>}
 */
export const fetchInboxLastMessages = (sdk, transactions = []) => {
  const lastMessages = {};
  return Promise.all(
    transactions.map(tx => {
      const id = tx?.id;
      const uuid = id?.uuid;
      if (!uuid) {
        return Promise.resolve();
      }
      return sdk.messages
        .query({
          transactionId: id,
          page: 1,
          perPage: 1,
        })
        .then(response => {
          lastMessages[uuid] = lastMessagePreview(response?.data?.data?.[0]);
        })
        .catch(() => {
          lastMessages[uuid] = '';
        });
    })
  ).then(() => lastMessages);
};

// ================ Slice ================ //

const inboxPageSlice = createSlice({
  name: 'InboxPage',
  initialState: {
    fetchInProgress: false,
    fetchOrdersOrSalesError: null,
    pagination: null,
    transactionRefs: [],
    lastMessages: {},
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadDataThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchOrdersOrSalesError = null;
      })
      .addCase(loadDataThunk.fulfilled, (state, action) => {
        const transactions = listFromQueryResponse(action.payload);
        state.fetchInProgress = false;
        state.fetchOrdersOrSalesError = null;
        state.transactionRefs = entityRefs(transactions);
        state.pagination = action.payload?.data?.meta || emptyInboxMeta();
        state.lastMessages = action.payload?.lastMessages || {};
      })
      .addCase(loadDataThunk.rejected, (state, action) => {
        console.error(action.payload || action.error);
        state.fetchInProgress = false;
        state.fetchOrdersOrSalesError = action.payload;
      });
  },
});

export default inboxPageSlice.reducer;

// ================ Load data ================ //

const loadDataPayloadCreator = ({ params, search }, { dispatch, rejectWithValue, extra: sdk }) => {
  const { tab } = params;

  const onlyFilterValues = {
    orders: 'order',
    sales: 'sale',
  };

  const onlyFilter = onlyFilterValues[tab];
  if (!onlyFilter) {
    return Promise.reject(new Error(`Invalid tab for InboxPage: ${tab}`));
  }

  const { page = 1, tx: highlightTxId, sort } = parse(search);
  const processNames = getSupportedProcessesInfo().map(p => p.name);
  const lastTransitions = inboxQueryLastTransitions(normalizeInboxSort(sort));

  const apiQueryParams = {
    only: onlyFilter,
    processNames,
    include: [
      'listing',
      'listing.images',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
      'reviews',
    ],
    'fields.review': ['rating', 'type', 'createdAt', 'state'],
    'fields.transaction': [
      'createdAt',
      'processName',
      'processVersion',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
      'protectedData',
      'metadata',
      'booking',
    ],
    'fields.booking': ['start', 'end', 'displayStart', 'displayEnd'],
    'fields.listing': [
      'title',
      'description',
      'state',
      'availabilityPlan',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
    ],
    'fields.user': [
      'profile.displayName',
      'profile.abbreviatedName',
      'profile.publicData',
      'deleted',
      'banned',
    ],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
      'variants.listing-card',
      'variants.listing-card-2x',
      'variants.default',
      'variants.scaled-small',
      'variants.scaled-medium',
    ],
    page,
    perPage: INBOX_PAGE_SIZE,
    // Newest first. Past (completed) also filters lastTransitions so
    // completed orders are not buried under cancelled holds on page 1.
    sort: '-lastTransitionedAt',
    ...(lastTransitions ? { lastTransitions } : {}),
  };

  const slimQueryParams = {
    only: onlyFilter,
    include: ['listing', 'listing.images', 'provider', 'customer', 'booking', 'reviews'],
    page,
    perPage: INBOX_PAGE_SIZE,
    sort: '-lastTransitionedAt',
    ...(lastTransitions ? { lastTransitions } : {}),
  };

  const bareQueryParams = {
    only: onlyFilter,
    page,
    perPage: INBOX_PAGE_SIZE,
  };

  const emptyPayload = () => ({
    data: { data: [], meta: emptyInboxMeta(page) },
    lastMessages: {},
  });

  return retryOn429(() => sdk.transactions.query(apiQueryParams), { retries: 1, jitter: 0 })
    .catch(error => {
      if (isTooManyRequestsError(error)) {
        throw error;
      }
      return sdk.transactions.query(slimQueryParams);
    })
    .catch(error => {
      if (isTooManyRequestsError(error)) {
        throw error;
      }
      return sdk.transactions.query(bareQueryParams);
    })
    .then(async response => {
      let listed = listFromQueryResponse(response);
      if (response?.data) {
        try {
          dispatch(addMarketplaceEntities(response));
        } catch (err) {
          // Empty or sparse SDK payloads must still render as an empty inbox.
        }
      }
      const pageNumber = Number(page) || 1;
      // All-sessions page 1 is 25 recently touched rows — usually past
      // reviews/cancels. Pin requested + accepted so Talker Edit booking
      // is on the first My Bookings screen, not page 2.
      if (pageNumber === 1 && !lastTransitions && sdk?.transactions?.query) {
        try {
          const upcomingRes = await retryOn429(
            () =>
              sdk.transactions.query({
                ...apiQueryParams,
                page: 1,
                lastTransitions: UPCOMING_LAST_TRANSITIONS,
              }),
            { retries: 1, jitter: 0 }
          );
          const extra = listFromQueryResponse(upcomingRes);
          if (extra.length) {
            try {
              dispatch(addMarketplaceEntities(upcomingRes));
            } catch (err) {
              // Still merge the raw list so Edit cards render.
            }
            listed = mergeInboxTransactions(extra, listed);
            response = {
              ...response,
              data: {
                ...(response?.data || {}),
                data: listed,
              },
            };
          }
        } catch (err) {
          // Recency page still renders if the upcoming pin 429s.
        }
      }
      const transactions = listed;
      const alreadyListed =
        typeof highlightTxId === 'string' &&
        highlightTxId &&
        transactions.some(tx => tx?.id?.uuid === highlightTxId);
      const pinMissingHighlight =
        typeof highlightTxId === 'string' &&
        highlightTxId &&
        !alreadyListed &&
        sdk?.transactions?.show
          ? sdk.transactions
              .show({
                id: highlightTxId,
                include: apiQueryParams.include,
                'fields.transaction': apiQueryParams['fields.transaction'],
                'fields.booking': apiQueryParams['fields.booking'],
                'fields.listing': apiQueryParams['fields.listing'],
                'fields.user': apiQueryParams['fields.user'],
                'fields.image': apiQueryParams['fields.image'],
                'fields.review': apiQueryParams['fields.review'],
              })
              .then(shown => {
                const extra = shown?.data?.data;
                if (extra) {
                  try {
                    dispatch(addMarketplaceEntities(shown));
                  } catch (err) {
                    // Still pin the raw transaction so the calendar deep-link renders.
                  }
                  return {
                    ...response,
                    data: {
                      ...(response?.data || {}),
                      data: [extra, ...transactions],
                    },
                  };
                }
                return response;
              })
              .catch(() => response)
          : Promise.resolve(response);
      return pinMissingHighlight.then(async nextResponse => {
        const nextTransactions = listFromQueryResponse(nextResponse);
        // Marketplace often omits Integration metadata.listenerFeedback.
        // Fill completed listener sales so Inbox can drop How did it go?
        const withFeedback =
          onlyFilter === 'sale'
            ? await attachInboxListenerFeedback(nextTransactions)
            : nextTransactions;
        if (withFeedback !== nextTransactions) {
          try {
            dispatch(
              addMarketplaceEntities({
                data: {
                  data: withFeedback,
                  included: nextResponse?.data?.included || [],
                },
              })
            );
          } catch (err) {
            // Still return the enriched list for this page render.
          }
        }
        // Inbox cards always show a status line. Do not query messages —
        // talk sessions have no chat outside Join, and N extra calls 429 TEST.
        return {
          data: {
            data: withFeedback,
            meta: nextResponse?.data?.meta || {
              ...emptyInboxMeta(page),
              totalItems: withFeedback.length,
            },
          },
          lastMessages: {},
        };
      });
    })
    .catch(e => {
      const stored = storableError(e);
      if (isTooManyRequestsError(e) || isTooManyRequestsError(stored)) {
        return rejectWithValue(stored);
      }
      if (isEmptyInboxFetchError(e) || isEmptyInboxFetchError(stored)) {
        return emptyPayload();
      }
      return rejectWithValue(stored);
    });
};

export const loadDataThunk = createAsyncThunk('InboxPage/loadData', loadDataPayloadCreator);

// Backward compatible wrapper for the thunk
export const loadData = (params, search) => (dispatch, getState, sdk) => {
  const inbox = dispatch(loadDataThunk({ params, search }));
  if (bookingsViewFromSearch(search) !== BOOKINGS_VIEW_CALENDAR) {
    return inbox;
  }
  return Promise.all([inbox, dispatch(loadDashboardData(params, search))]);
};
